<svelte:options
	customElement={{
		tag: "ofc-energy-label-v1",
		/*                                            */
		extend: window.__components.extend(),
		props: {
			asButton: { type: "Boolean", attribute: "as-button" },
			base64Href: { type: "String", attribute: "base64-href" },
			color: { type: "String" },
			rel: { type: "String" },
			size: { type: "String" },
			target: { type: "String" },
			variant: { type: "String" },
			version: { type: "String" },
		},
	}}
/>

<script lang="ts">
	import type { Props } from "./EnergyLabelV1.types";

	const EEK_PREFIX = "pl_eek2024";
	const EEK_ASSETS_URL_PREFIX = "/assets-static/icons/";
	const VALID_SIZES: string[] = ["100", "200"];
	const VALID_VERSIONS: string[] = ["default", "uniform"];
	const MAX_PLUS_COUNTS: Record<string, number | undefined> = {
		default: 3,
		uniform: 0,
	};

	let {
		size = "100",
		variant = "A",
		color = "#33a357",
		version = "default",
		asButton = false,
		rel = undefined,
		target = undefined,
		base64Href,
	}: Props = $props();

	function svgUrl(svgId: string): string {
		return `${EEK_ASSETS_URL_PREFIX}${svgId}.svg`;
	}

	function svgIdentifier(
		label: string,
		labelSize: string,
		labelVersion: string,
	): string | undefined {
		if (!VALID_VERSIONS.includes(labelVersion)) return undefined;
		if (!VALID_SIZES.includes(labelSize)) return undefined;

		const plusCount = label.split("+").length - 1;
		if (plusCount > (MAX_PLUS_COUNTS[labelVersion] ?? 0)) return undefined;

		if (plusCount > 0) {
			return `${EEK_PREFIX}_${labelVersion}_${label.toLowerCase()[0]}${plusCount}_${labelSize}`;
		}
		return `${EEK_PREFIX}_${labelVersion}_${label.toLowerCase()}_${labelSize}`;
	}

	let svgId = $derived(svgIdentifier(variant!, size!, version!));
	let url = $derived(svgId ? svgUrl(svgId) : undefined);
	let labelChar = $derived(variant);
	let isInteractive = $derived(asButton || !!base64Href);
	let title = $derived(
		`Energieverbrauchskennzeichnung ${labelChar}${isInteractive ? ". Informationen anzeigen." : ""}`,
	);
</script>

{#snippet label()}
	{#if !!url}
		<span class="fallback oc-copy-100" aria-hidden="true">{labelChar}</span>
		<svg role="img" width="36" height="20">
			<title>{title} </title>
			<use href="{url}#{svgId}" />
		</svg>
	{/if}
{/snippet}

{#if asButton}
	<button
		class="energy-label energy-label--size-{size}"
		style="--energy-label-background-color: {color}"
		aria-label={title}
	>
		{@render label()}
	</button>
{:else if !!base64Href}
	<oc-interactive-overlay-v1
		class="energy-label energy-label--size-{size}"
		style="--energy-label-background-color: {color}"
		base64-href={base64Href}
		{rel}
		{target}
	>
		{@render label()}
	</oc-interactive-overlay-v1>
{:else}
	<div
		class="energy-label energy-label--size-{size}"
		style="--energy-label-background-color: {color}"
	>
		{@render label()}
	</div>
{/if}

<style lang="scss">
	@use "@otto-ec/design-tokens/tokens";
	@use "@otto-ec/otto-components-utils/scss/mixins";

	/**
 *
 */
	$magic-padding-size-100: 0.5rem;
	$magic-padding-size-200: 0.9rem;

	:host {
		display: inline-block;
		vertical-align: middle;
	}

	:global {
		.energy-label {
			background: unset;
			position: relative;
			display: block;
			padding: 0;
			border: none;
			outline: 0 none;
			@include mixins.hitbox(tokens.$oc-base-dimension-24);

			.fallback {
				display: inline-block;
				position: absolute;
				top: 0;
				right: 0;
				width: 30px;
				text-align: center;
				vertical-align: middle;
				white-space: nowrap;
				overflow: hidden;
			}

			> svg {
				position: relative;

				display: block;
			}

			&--size-100 {
				svg {
					width: 2.25rem;
					height: calc(
						#{tokens.$oc-base-dimension-relative-24} - 0.25rem
					); /*     */
				}
			}

			&--size-200 {
				svg {
					width: 4rem;
					height: calc(
						#{tokens.$oc-base-dimension-relative-40} - 0.25rem
					); /*     */
				}
			}

			use {
				color: var(--energy-label-background-color, gray);
			}
		}
	}
</style>
